import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import { Popper } from "@mui/base/Popper";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  PaymentsSubscriptionCancelAttributes,
  PaymentsSubscriptionUpdateAttributes,
} from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { Subscription, TeamUsage } from "~/api/types";
import { Icon } from "~/assets/icons/Icon";
import { isAutoptoTrialing } from "~/components/autopto/isAutoptoTrial";
import Addons from "~/components/subscription/Addons";
import PayAsYouGo from "~/components/subscription/PayAsYouGo";
import PaymentMethods from "~/components/subscription/PaymentMethods";
import PlanUsage from "~/components/subscription/PlanUsage";
import {
  getAddondTableComponentData,
  getPayAsYouGoComponentDataFreeLimits,
  getPayAsYouGoComponentDataNoFreeLimits,
  getPaymentMethodsTableComponentData,
  getPlanUsageComponentData,
} from "~/components/subscription/helpers";
import {
  isSubscriptionStatusActivated,
  isSubscriptionStatusPending,
} from "~/components/subscription/helpers";
import { SavedCardStatus } from "~/components/subscription/useSavedCardRecheck";
import Card from "~/components/ui/card/Card";
import Modal from "~/components/ui/modal/Modal";
import { useTeam } from "~/contexts/TeamProvider";
import { STRIPE_SESSION_AUTO_OPEN_KEY } from "~/helpers/consts";
import { DATE_FORMAT } from "~/helpers/helpers";
import labels from "~/helpers/labels";
import { stripePromise } from "~/helpers/load_stripe";
import { useCableSubscription } from "~/hooks/useCableSubscription";
import { useEndpointAccess } from "~/hooks/useEndpointAccess";
import { useStripeSession } from "~/hooks/useStripeSession";
import styles from "~/pages/billing/Billing.module.scss";
import { routes } from "~/router";

import { MappedData } from "../../pages/billing/SubscriptionPage";
import Button from "../mantine/button/Button";
import Spinner from "../ui/spinner/Spinner";
import CancelationFormContent from "./modals/CancelationFormContent";
import EditSubscriptionContent from "./modals/EditSubsriptionContent";
import ReasonToGoFormContent from "./modals/ReasonToGoFormContent";

const standbyabyText =
  "Alternatively, you can switch your subscription into a Standby Mode paying as little as 25% of your current plan. We will save all your data, including Sites, Keywords, Automations, and will continue optimizing your sites.";

export type CancelReason = NonNullable<
  PaymentsSubscriptionCancelAttributes["cancel_reason"]
>;

export type FormValues = {
  confirmation: string;
  cancel_comment: string;
  cancel_reason: CancelReason;
};

export type WithSubscriptionProps = {
  data: {
    planUsageData: { team_usage: TeamUsage };
    subscriptionData: Subscription;
  };
  // Opens the app's card dialog (CardDialogProvider).
  strapiSetup: () => void;
  updateSubscription: (data: PaymentsSubscriptionUpdateAttributes) => void;
  editSubscriptionData: MappedData;
  isPendingUpdateSubscription: boolean;
  setOpenPlanModal: (value: boolean) => void;
  openPlanModal: boolean;
  isFetching: boolean;
  // False when the backend refuses the add-on products (403).
  isAddonsAvailable: boolean;
  isCreatingSession: boolean;
  // A saved card awaits its webhook (the card dialog stays shut).
  isAwaitingCard: boolean;
  savedCardStatus: SavedCardStatus;
  isSubscriptionUpdate: boolean;
  subscriptionAuthorizations: {
    member: string[];
  };
};

const WithSubscription = ({
  data,
  strapiSetup,
  editSubscriptionData,
  updateSubscription,
  openPlanModal,
  setOpenPlanModal,
  isPendingUpdateSubscription,
  isFetching,
  isAddonsAvailable,
  isCreatingSession,
  isAwaitingCard,
  savedCardStatus,
  isSubscriptionUpdate,
  subscriptionAuthorizations,
}: WithSubscriptionProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const subscription = data?.subscriptionData?.["payments/subscription"];
  const planUsage = data?.planUsageData?.team_usage;
  const isTeamCreated = subscription?.status === "created";
  const paymentMethodsData = subscription?.payment_method;
  const isSubscriptionNotCancelled = subscription?.cancel_at_period_end;
  const isStandbyable = subscription?.standbyable;
  const planUsageComponentData = getPlanUsageComponentData(
    planUsage,
    subscription,
  );
  const currentSubscription = {
    price: subscription?.price.amount,
    name: subscription?.price.product.name,
    status: subscription?.status,
    interval: subscription?.price.recurring.interval,
    valid: subscription?.period_end_at
      ? `Valid until ${format(subscription?.period_end_at, "MMMM dd',' yyyy")}`
      : "",
  };
  const paymentMethodsTableComponentData = paymentMethodsData
    ? getPaymentMethodsTableComponentData(paymentMethodsData)
    : null;
  const addondTableComponentData = getAddondTableComponentData(
    subscription,
    planUsage,
  );
  const payAsYouGoDataFreeLimits = getPayAsYouGoComponentDataFreeLimits(
    subscription?.subscription_addons || [],
    planUsage,
  );
  const payAsYouGoDataNoFreeLimits = getPayAsYouGoComponentDataNoFreeLimits(
    subscription?.subscription_addons || [],
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  const {
    clientSecret: checkoutClientSecret,
    isModalOpen: isCheckoutOpen,
    isCreatingSession: isCreatingCheckoutSession,
    openSession: openCheckoutSession,
    closeModal: closeCheckoutModal,
    finishModal: finishCheckoutModal,
  } = useStripeSession({
    sessionType: "checkout",
    onSuccess: () => {},
    onError: () => {},
  });

  const isCheckoutAuthorized = Boolean(
    subscriptionAuthorizations?.member?.includes("checkout"),
  );

  const handleProceedToCheckout = () => {
    if (isCheckoutAuthorized) {
      openCheckoutSession();
      localStorage.setItem(STRIPE_SESSION_AUTO_OPEN_KEY, "1");
    }
  };
  const [checkoutPopupDidOpen, setCheckoutPopupDidOpen] = useState(false);
  const isStatusPending =
    !!subscription?.status && isSubscriptionStatusPending(subscription.status);

  // The subscription status flips (incomplete -> trialing) only after the
  // Stripe webhook is processed on the backend, so until the
  // SubscriptionsChannel delivers the change we show an "activating"
  // state instead of the stale "Proceed to Checkout" button.
  // The local flag reacts to onComplete instantly; the server-side
  // `awaiting_activation` (completed checkout session + still-pending
  // subscription) covers page reloads inside that window.
  const [didCompleteCheckout, setDidCompleteCheckout] = useState(false);
  const isAwaitingActivation =
    didCompleteCheckout || Boolean(subscription?.awaiting_activation);
  const { team, teamId, refetchTeam } = useTeam();

  const handleCheckoutComplete = () => {
    finishCheckoutModal();
    setDidCompleteCheckout(true);
  };

  useCableSubscription(
    "SubscriptionsChannel",
    teamId != null ? { team_id: teamId } : {},
    {
      // A (re)connect may have missed the status-change broadcast —
      // refetch a pending subscription so the state catches up.
      connected: () => {
        if (!isStatusPending) return;

        void queryClient.invalidateQueries({
          queryKey: ["subscriptionPlans"],
        });
        void refetchTeam();
      },
      received: (message) => {
        if (message.action !== "status-changed") return;

        void queryClient.invalidateQueries({
          queryKey: ["subscriptionPlans"],
        });
        void refetchTeam();

        const { status } = message.payload;

        if (isSubscriptionStatusPending(status)) return;

        // The status is final — if the checkout modal is somehow still
        // open (e.g. reopened by a reload race), close it: the session
        // is consumed and Stripe would only show a dead-end screen.
        finishCheckoutModal();

        if (!isAwaitingActivation) return;

        if (isSubscriptionStatusActivated(status)) {
          enqueueSnackbar("Your subscription is now active!", {
            variant: "success",
          });
        } else {
          enqueueSnackbar(
            "There was a problem activating your subscription. Please check your payment method or contact support.",
            { variant: "error", persist: true },
          );
        }
      },
    },
  );

  // Drop the local flag only once the refetched subscription itself
  // reports a final status. Clearing it right in the WS handler re-rendered
  // against the still-stale pending status and flashed the "Proceed to
  // Checkout" button for the duration of the refetch round-trip.
  useEffect(() => {
    if (!didCompleteCheckout) return;
    if (!subscription?.status || isStatusPending) return;

    setDidCompleteCheckout(false);
  }, [didCompleteCheckout, isStatusPending, subscription?.status]);

  // open checkout modal right after a subscription was created
  useEffect(() => {
    if (
      !checkoutPopupDidOpen &&
      location.state?.openCheckout &&
      isCheckoutAuthorized &&
      isStatusPending &&
      !isAwaitingActivation
    ) {
      handleProceedToCheckout();
      setCheckoutPopupDidOpen(true);
      // Consume the one-shot intent: history state survives page reloads,
      // and a reload right after payment must not reopen the checkout.
      navigate(`${location.pathname}${location.search}`, { replace: true });
    }
  }, [
    checkoutPopupDidOpen,
    location.state,
    isStatusPending,
    isAwaitingActivation,
    isCheckoutAuthorized,
  ]);

  // One-time auto open checkout model,
  // when a user has just come from WP landing from /pricing tab and a subscription is already created
  useEffect(() => {
    if (
      !isCheckoutAuthorized ||
      !isStatusPending ||
      isAwaitingActivation ||
      isFetching ||
      localStorage.getItem(STRIPE_SESSION_AUTO_OPEN_KEY)
    )
      return;

    handleProceedToCheckout();
  }, [isStatusPending, isAwaitingActivation, isFetching, isCheckoutAuthorized]);

  const handleChangePlan = () => {
    setOpenPlanModal(true);
  };
  const isCancelSubscription =
    subscriptionAuthorizations?.member?.includes("cancel");
  const isRestoreSubscription =
    subscriptionAuthorizations?.member?.includes("restore");

  // Until the authorizations arrive the controls show disabled; then the
  // backend decides (e.g. during an Autopto trial only the card is allowed).
  // The card also carries "Proceed to Checkout": keep it when that's all
  // the backend allows.
  const isAuthorizationsLoaded = Boolean(subscriptionAuthorizations);
  // Until then, expect them closed where the backend closes them (the
  // whole Autopto trial, a card or not, and a blocked subscription) or while
  // the team is unknown, so the card doesn't flash.
  const isPlanControlExpected =
    Boolean(team) &&
    !isAutoptoTrialing(team) &&
    !team?.subscription?.blocked_reason;
  const showPlanControls = isAuthorizationsLoaded
    ? Boolean(isSubscriptionUpdate)
    : isPlanControlExpected;
  const showPlanMenu = isAuthorizationsLoaded
    ? Boolean(isCancelSubscription || isRestoreSubscription)
    : isPlanControlExpected;
  const showPlanCard = showPlanControls || showPlanMenu || isCheckoutAuthorized;

  // The usage estimate lives with the invoices: no invoices, no estimate.
  // The metered add-ons come from the catalog, which a trial or a blocked
  // subscription can't reach (403), so they hide with the add-ons too.
  const canOpenInvoices = useEndpointAccess("invoices");
  const {
    mutate: subscriptionSuspendCreate,
    isPending: isPendingSubscriptionSuspendCreate,
  } = useMutation({
    mutationFn: async () => {
      const response = await apiClient.billing.subscriptionSuspendCreate();

      return response;
    },
    onSuccess: async () => {
      enqueueSnackbar("Subscription was successfully suspended", {
        variant: "success",
      });
      // Standby replaces the cancelation, so the cancel dialog is done.
      handleCloseCancelModal();

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["subscriptionData"] }),
        queryClient.invalidateQueries({ queryKey: ["checkout-authorization"] }),
        queryClient.invalidateQueries({ queryKey: ["subscriptionPlans"] }),
      ]);
    },
    onError: (error) => {
      const errorMessage =
        //@ts-expect-error BE types
        error?.error?.error?.message || "Failed to suspend subscription";
      enqueueSnackbar(`Error: ${errorMessage}`, {
        variant: "error",
        persist: true,
      });
    },
  });

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (data: FormValues) => {
      const response = await apiClient.billing.subscriptionCancelCreate(data);

      return response;
    },
    onSuccess: async () => {
      enqueueSnackbar("Subscription was successfully canceled", {
        variant: "success",
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["subscriptionPlans"] }),
        queryClient.invalidateQueries({ queryKey: ["checkout-authorization"] }),
        queryClient.invalidateQueries({ queryKey: ["subscriptionData"] }),
      ]);

      setCancelSuccess(false);
      setOpenCancelModal(false);
      reset();
    },
    onError: (error) => {
      const errorMessage =
        //@ts-expect-error BE types
        error?.error?.error?.message || "Failed to cancel subscription";
      enqueueSnackbar(`Error: ${errorMessage}`, {
        variant: "error",
        persist: true,
      });
    },
  });

  const {
    mutate: restoreSubscriptionMutation,
    isPending: restoreSubscriptionPending,
  } = useMutation({
    mutationFn: async () => {
      const response = await apiClient.billing.subscriptionRestoreCreate();

      return response;
    },
    onSuccess: async () => {
      setAnchorEl(null);
      enqueueSnackbar("Subscription was successfully restored", {
        variant: "success",
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["subscriptionData"] }),
        queryClient.invalidateQueries({ queryKey: ["checkout-authorization"] }),
        queryClient.invalidateQueries({ queryKey: ["subscriptionPlans"] }),
      ]);
    },
    onError: (error) => {
      console.error("Failed to restore subscription:", error);

      const errorMessage =
        //@ts-expect-error BE types
        error?.error?.error?.message || "Failed to restore subscription";
      enqueueSnackbar(`Error: ${errorMessage}`, {
        variant: "error",
        persist: true,
      });
    },
  });

  const restoreSubscription = () => {
    restoreSubscriptionMutation();
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const form = useForm<FormValues>();
  const { getValues, watch, reset } = form;
  watch("confirmation");

  const openPopper = Boolean(anchorEl);
  const popperId = openPopper ? "simple-popper" : undefined;

  const handleOpenCancelModal = () => {
    setOpenCancelModal(true);
    setCancelSuccess(false);
  };

  const handleCloseCancelModal = () => {
    setAnchorEl(null);
    setOpenCancelModal(false);
    reset();
    setCancelSuccess(false);
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    setCancelSuccess(true);
  };

  const onSubmitFeedbackModal: SubmitHandler<FormValues> = (data) => {
    const payload = {
      cancel_comment: data.cancel_comment,
      ...(data.cancel_reason && { cancel_reason: data.cancel_reason }),
    };
    //@ts-expect-error BE types
    mutate(payload);
  };

  const isSubmitDisabled =
    getValues()?.confirmation !== labels.CANCEL_SUBSCRIPTION;
  const subscriptionEndDate = subscription?.period_end_at
    ? format(subscription.period_end_at, DATE_FORMAT)
    : "";
  //@ts-expect-error BE types
  const cancelError = error?.error?.error?.message;

  const combinedModalLabel = cancelSuccess ? (
    <h3 className={styles.modalTitle}>{labels.BEFORE_YOU_GO}</h3>
  ) : (
    <h3 className={styles.modalTitle}>
      <div className={styles.dangerIconWrapper}>
        <Icon iconName="DangerIcon" width="1rem" height="1rem" />
      </div>
      {labels.CANCEL_SUBSCRIPTION}
    </h3>
  );

  const combinedModalButtonLabel = cancelSuccess
    ? labels.SUBMIT_FEEDBACK
    : labels.CANCEL_SUBSCRIPTION;

  const combinedModalButtonStyle = cancelSuccess ? "primary" : "danger";

  return (
    <>
      <div className={styles.cardsWrapper}>
        {showPlanCard && (
          <Card
            title={labels.YOUR_PLAN_USAGE}
            button={
              <div className={styles.headerButtonsWrapper}>
                {showPlanControls && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleChangePlan}
                      disabled={!isSubscriptionUpdate}
                    >
                      <span className={styles.editSubscriptionButton}>
                        <Icon iconName="EditIcon" width="1rem" height="1rem" />
                        {labels.EDIT_SUBSCRIPTION}
                      </span>
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        navigate(routes.billing.subscriptionEdit())
                      }
                      disabled={!isSubscriptionUpdate}
                    >
                      <span className={styles.editSubscriptionButton}>
                        <Icon iconName="EditIcon" width="1rem" height="1rem" />
                        {labels.CHANGE_PLAN}
                      </span>
                    </Button>
                  </>
                )}
                {showPlanMenu && (
                  <Button
                    variant="outlined"
                    // @ts-expect-error TS2551 non-existing className
                    className={styles.unstyledEditSubscriptionButton}
                    onClick={handleClick}
                    disabled={!isCancelSubscription && !isRestoreSubscription}
                  >
                    <Icon
                      iconName="TreeDotsButtonIcon"
                      width="1rem"
                      height="1rem"
                    />
                  </Button>
                )}
              </div>
            }
          >
            <PlanUsage
              isFetching={isFetching}
              data={planUsageComponentData}
              isStatusPending={isStatusPending}
              isAwaitingActivation={isAwaitingActivation}
              handleProceedToCheckout={handleProceedToCheckout}
            />
          </Card>
        )}
        <Card
          title="Payment Methods"
          button={
            !isTeamCreated && (
              <div className={styles.headerButtonsWrapper}>
                <Button
                  variant="outlined"
                  onClick={strapiSetup}
                  disabled={isCreatingSession || isAwaitingCard}
                >
                  <span className={styles.editSubscriptionButton}>
                    <Icon
                      iconName={paymentMethodsData ? "EditIcon" : "PlusIcon"}
                      width="1rem"
                      height="1rem"
                    />
                    {paymentMethodsData
                      ? labels.UPDATE_PAYMENT_METHOD
                      : labels.ADD_PAYMENT_METHOD}
                  </span>
                </Button>
              </div>
            )
          }
        >
          <PaymentMethods
            data={paymentMethodsTableComponentData}
            isFetching={isFetching}
            savedCardStatus={savedCardStatus}
            isAwaitingCard={isAwaitingCard}
          />
        </Card>
        {isAddonsAvailable && (
          <Card title="Add-ons">
            <Addons data={addondTableComponentData} />
          </Card>
        )}
        {canOpenInvoices &&
          isAddonsAvailable &&
          (payAsYouGoDataFreeLimits?.length > 0 ||
            payAsYouGoDataNoFreeLimits?.length > 0) && (
            <Card
              title="Pay As You Go"
              button={
                <Button
                  variant="outlined"
                  component={Link}
                  to={routes.billing.invoices()}
                >
                  <span className={styles.editSubscriptionButton}>
                    <Icon iconName="InvoiceIcon" width="1rem" height="1rem" />
                    {labels.VIEW_INVOICE_ESTIMATE}
                  </span>
                </Button>
              }
            >
              <PayAsYouGo
                dataNoFreeLimits={payAsYouGoDataNoFreeLimits}
                dataFreeLimits={payAsYouGoDataFreeLimits}
              />
            </Card>
          )}
      </div>
      <Popper
        id={popperId}
        open={openPopper}
        anchorEl={anchorEl}
        placement="bottom-end"
      >
        {!isSubscriptionNotCancelled ? (
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Button
              variant="noHover"
              className={styles.popperWrapper}
              onClick={() => setAnchorEl(null)}
              disabled={!isCancelSubscription}
            >
              <div
                className={styles.popoverItem}
                onClick={handleOpenCancelModal}
                role="button"
              >
                {labels.CANCEL_SUBSCRIPTION}
              </div>
            </Button>
          </ClickAwayListener>
        ) : (
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Button
              variant="noHover"
              className={styles.popperWrapper}
              onClick={() => setAnchorEl(null)}
              disabled={!isRestoreSubscription}
            >
              <div
                className={styles.popoverItem}
                onClick={restoreSubscription}
                role="button"
              >
                <div className={styles.withSpinner}>
                  {restoreSubscriptionPending && <Spinner size="1rem" />}
                  {labels.RESTORE_SUBSCRIPTION}
                </div>
              </div>
            </Button>
          </ClickAwayListener>
        )}
      </Popper>
      <Modal
        key="cancelationModal"
        isOpen={openCancelModal}
        closeModal={handleCloseCancelModal}
        formId="cancelationModalForm"
        modalLabel={combinedModalLabel}
        buttonLabel={combinedModalButtonLabel}
        isDisabled={isSubmitDisabled}
        actionButtonColor={combinedModalButtonStyle}
        isLoading={isPending}
        action={() => onSubmit}
      >
        {cancelSuccess ? (
          <ReasonToGoFormContent
            form={form}
            cancelError={cancelError}
            onSubmitFeedbackModal={onSubmitFeedbackModal}
          />
        ) : (
          <CancelationFormContent
            form={form}
            onSubmit={onSubmit}
            subscriptionEndDate={subscriptionEndDate}
            standbyable={isStandbyable ? standbyabyText : null}
            onTurnOnStandby={() => subscriptionSuspendCreate()}
            isStandbyPending={isPendingSubscriptionSuspendCreate}
          />
        )}
      </Modal>
      <Modal
        key="changePlanModal"
        isOpen={openPlanModal}
        closeModal={() => setOpenPlanModal(false)}
        formId="changePlanModal"
        height="auto"
        width="330px"
        modalLabel={<h3 className={styles.modalTitle}>Edit Subscription</h3>}
      >
        <EditSubscriptionContent
          planUsage={planUsage}
          editSubscriptionData={editSubscriptionData}
          currentSubscription={currentSubscription}
          onUpdatePlan={updateSubscription}
          isPendingUpdateSubscription={isPendingUpdateSubscription}
        />
      </Modal>
      <Modal
        key="checkout"
        isOpen={isCheckoutOpen}
        closeModal={closeCheckoutModal}
        formId="checkout"
        height="92%"
        width="80%"
        modalLabel={<h3 className={styles.modalTitle}></h3>}
        forceWhiteBackground
      >
        {isCreatingCheckoutSession && <Spinner />}
        {checkoutClientSecret && stripePromise && (
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{
              clientSecret: checkoutClientSecret,
              onComplete: handleCheckoutComplete,
            }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        )}
      </Modal>
    </>
  );
};

export default WithSubscription;
